import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Beaker,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Loader2,
  Clock,
  Target,
  Trophy,
  Send,
  RotateCcw,
  Home,
  CheckSquare,
  Square,
  AlertTriangle,
  Zap,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const LabPage = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [currentInput, setCurrentInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [optionConsequence, setOptionConsequence] = useState(null);
  const [showConsequence, setShowConsequence] = useState(false);

  useEffect(() => {
    loadLab();
  }, [labId]);

  const loadLab = async () => {
    try {
      const response = await api.getLabDetail(labId);
      setLab(response.data);
      // Check if user has previous progress
      if (response.data.user_progress) {
        setUserResponses(response.data.user_progress.responses || {});
        setCurrentStep(response.data.user_progress.current_step || 0);
        if (response.data.user_progress.completed) {
          setCompleted(true);
        }
      }
    } catch (error) {
      toast.error('Could not load lab');
      navigate('/labs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStep = async () => {
    if (!currentInput.trim()) {
      toast.error('Please enter your response');
      return;
    }

    setSubmitting(true);
    try {
      const stepId = lab.steps[currentStep].id;
      const newResponses = { ...userResponses, [stepId]: currentInput };
      setUserResponses(newResponses);

      // Save progress to backend
      await api.saveLabProgress(labId, {
        current_step: currentStep,
        responses: newResponses,
        completed: false
      });

      // Get AI feedback for this step
      const feedbackResponse = await api.getLabStepFeedback(labId, stepId, currentInput);
      setFeedback(feedbackResponse.data.feedback);

      // Move to next step after short delay
      setTimeout(() => {
        if (currentStep < lab.steps.length - 1) {
          setCurrentStep(currentStep + 1);
          setCurrentInput('');
          setFeedback(null);
        } else {
          // Lab completed
          handleCompleteLab(newResponses);
        }
      }, 2000);
    } catch (error) {
      toast.error('Could not save progress');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteLab = async (responses) => {
    try {
      await api.completeLab(labId, { responses });
      setCompleted(true);
      toast.success('Lab completed! Great work!');
    } catch (error) {
      console.error('Error completing lab:', error);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      const prevStepId = lab.steps[currentStep - 1].id;
      const prevResponse = userResponses[prevStepId];
      
      // Handle both text and option responses
      if (typeof prevResponse === 'object' && prevResponse?.option_id) {
        setSelectedOption(prevResponse);
        setCurrentInput('');
      } else {
        setCurrentInput(prevResponse || '');
        setSelectedOption(null);
      }
      
      setFeedback(null);
      setOptionConsequence(null);
      setShowConsequence(false);
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setUserResponses({});
    setCurrentInput('');
    setCompleted(false);
    setFeedback(null);
    setSelectedOption(null);
    setOptionConsequence(null);
    setShowConsequence(false);
  };

  // Handle option selection for decision-tree steps
  const handleOptionSelect = async (option) => {
    setSelectedOption(option);
    setShowConsequence(true);
    
    // Generate consequence feedback based on selection
    const consequence = getOptionConsequence(option);
    setOptionConsequence(consequence);
  };

  // Get consequence text based on option type
  const getOptionConsequence = (option) => {
    const consequences = {
      // Crisis Lab consequences
      'cash_crisis': {
        title: "Cash Crisis Activated",
        description: "You're simulating a runway crisis. With 3 months left, every decision matters. Let's work through this step by step.",
        severity: "high",
        nextHint: "In the next step, you'll assess the full scope of your cash situation."
      },
      'revenue_crisis': {
        title: "Revenue Crisis Activated",
        description: "Losing 30% of revenue is serious. But many founders have recovered from worse. Let's build your response plan.",
        severity: "high",
        nextHint: "Next, we'll assess the damage and understand who else might be at risk."
      },
      'team_crisis': {
        title: "Team Crisis Activated",
        description: "Co-founder departures can break or strengthen a company. How you handle this defines your leadership.",
        severity: "medium",
        nextHint: "Let's assess what they're taking with them and what needs to transition."
      },
      'pr_crisis': {
        title: "PR Crisis Activated",
        description: "A security breach is every founder's nightmare. Speed and transparency are crucial here.",
        severity: "critical",
        nextHint: "First priority: understand the full scope. What data was exposed? Who's affected?"
      },
      'product_crisis': {
        title: "Product Crisis Activated",
        description: "The hardest realization: your current path isn't working. But pivots have created some of the best companies.",
        severity: "medium",
        nextHint: "Let's assess what's not working and what signals are telling you to change."
      },
      // Values Reflection Lab consequences
      'funding_dilemma': {
        title: "Funding Dilemma Selected",
        description: "Money comes with expectations. This scenario explores the tension between growth capital and founder control.",
        severity: "info",
        nextHint: "Next, you'll identify which of your core values might be compromised by taking this money."
      },
      'people_dilemma': {
        title: "People Dilemma Selected",
        description: "Personal relationships and professional decisions can conflict. This is one of the hardest founder challenges.",
        severity: "medium",
        nextHint: "We'll explore how your values around loyalty, honesty, and company duty interact."
      },
      'work_life_dilemma': {
        title: "Work-Life Dilemma Selected",
        description: "The startup grind can consume everything. This scenario helps you find your boundaries.",
        severity: "info",
        nextHint: "You'll map your values around family, ambition, health, and success."
      },
      'vision_dilemma': {
        title: "Vision Dilemma Selected",
        description: "Sometimes the market tells you your original idea needs to change. Is pivoting strength or weakness?",
        severity: "info",
        nextHint: "We'll explore your values around conviction, adaptability, and ego."
      },
      'quality_dilemma': {
        title: "Quality Dilemma Selected",
        description: "Ship fast or ship right? This tension defines many startup decisions.",
        severity: "info",
        nextHint: "Your values around craftsmanship, speed, and customer experience will be tested."
      },
      // Default consequence for options without specific types
      'default': {
        title: `Option ${option.id} Selected`,
        description: option.text,
        severity: "info",
        nextHint: "This choice will shape the rest of your lab experience."
      }
    };
    
    return consequences[option.type] || consequences['default'];
  };

  // Handle submitting decision-tree step with option
  const handleSubmitOptionStep = async () => {
    if (!selectedOption) {
      toast.error('Please select an option');
      return;
    }

    setSubmitting(true);
    try {
      const stepId = lab.steps[currentStep].id;
      const responseData = {
        option_id: selectedOption.id,
        option_text: selectedOption.text,
        option_type: selectedOption.type || null
      };
      
      const newResponses = { ...userResponses, [stepId]: responseData };
      setUserResponses(newResponses);

      // Save progress to backend
      await api.saveLabProgress(labId, {
        current_step: currentStep,
        responses: newResponses,
        completed: false,
        decision_path: selectedOption.type || selectedOption.id
      });

      // Move to next step
      setTimeout(() => {
        if (currentStep < lab.steps.length - 1) {
          setCurrentStep(currentStep + 1);
          setCurrentInput('');
          setFeedback(null);
          setSelectedOption(null);
          setOptionConsequence(null);
          setShowConsequence(false);
        } else {
          handleCompleteLab(newResponses);
        }
      }, 1500);
    } catch (error) {
      toast.error('Could not save progress');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if current step has options (decision-tree)
  const isDecisionTreeStep = lab?.steps?.[currentStep]?.options && lab.steps[currentStep].options.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Beaker className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t('labs.notFound')}</h2>
        <Button onClick={() => navigate('/labs')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('labs.backToLabs')}
        </Button>
      </div>
    );
  }

  const currentStepData = lab.steps[currentStep];
  const progress = ((currentStep + 1) / lab.steps.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/labs')}
                className="text-muted-foreground hover:text-foreground"
                data-testid="back-to-labs"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-mint flex items-center justify-center">
                  <Beaker className="w-5 h-5 text-ocean-blue" />
                </div>
                <div>
                  <h1 className="font-semibold text-foreground text-sm sm:text-base line-clamp-1">
                    {lab.title}
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Step {currentStep + 1} of {lab.steps.length}
                  </p>
                </div>
              </div>
            </div>
            <Badge variant="outline" className="hidden sm:flex">
              {lab.difficulty}
            </Badge>
          </div>
          {/* Progress Bar */}
          <div className="pb-3">
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <AnimatePresence mode="wait">
          {completed ? (
            /* Completion Screen */
            <motion.div
              key="completed"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center py-12"
            >
              <div className="w-20 h-20 rounded-full bg-mint mx-auto mb-6 flex items-center justify-center">
                <Trophy className="w-10 h-10 text-ocean-blue" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {t('labs.labCompleted')}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {t('labs.greatWork')}
              </p>
              
              {/* Summary of responses */}
              <Card className="text-left mb-8 max-w-2xl mx-auto">
                <CardHeader>
                  <CardTitle className="text-lg">{t('labs.yourResponses')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {lab.steps.map((step, index) => (
                    <div key={step.id} className="border-b border-border pb-4 last:border-0 last:pb-0">
                      <p className="font-medium text-sm text-foreground mb-1">
                        {index + 1}. {step.title}
                      </p>
                      {typeof userResponses[step.id] === 'object' && userResponses[step.id]?.option_text ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {t('labs.option')} {userResponses[step.id].option_id}
                          </Badge>
                          <p className="text-sm text-muted-foreground">
                            {userResponses[step.id].option_text}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {userResponses[step.id] || t('labs.noResponse')}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  onClick={handleRestart}
                  data-testid="restart-lab"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t('labs.restartLab')}
                </Button>
                <Button 
                  onClick={() => navigate('/labs')}
                  className="gradient-ocean"
                  data-testid="back-to-labs-complete"
                >
                  <Home className="w-4 h-4 mr-2" />
                  {t('labs.backToLabs')}
                </Button>
              </div>
            </motion.div>
          ) : (
            /* Active Step */
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Emotional Tone Banner */}
              {lab.emotional_tone && currentStep === 0 && (
                <div className="bg-mint/30 rounded-xl p-4 mb-6">
                  <p className="text-sm text-ocean-blue italic text-center">
                    "{lab.emotional_tone}"
                  </p>
                </div>
              )}

              {/* Step Card */}
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-ocean-blue text-white flex items-center justify-center text-sm font-bold">
                      {currentStep + 1}
                    </div>
                    <Badge variant="outline">{currentStepData.title}</Badge>
                  </div>
                  <CardTitle className="text-xl">
                    {currentStepData.prompt}
                  </CardTitle>
                  {currentStepData.description && (
                    <CardDescription>
                      {currentStepData.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Scenario Context */}
                  {currentStepData.scenario && (
                    <div className="bg-muted/50 rounded-lg p-4 mb-4">
                      <p className="text-sm text-foreground leading-relaxed">
                        {currentStepData.scenario}
                      </p>
                    </div>
                  )}

                  {/* Guidance */}
                  {currentStepData.guidance && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-ocean-blue mb-2">Guidance:</p>
                      <p className="text-sm text-muted-foreground">
                        {currentStepData.guidance}
                      </p>
                    </div>
                  )}

                  {/* Examples */}
                  {currentStepData.examples && currentStepData.examples.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-ocean-blue mb-2">Examples:</p>
                      <ul className="space-y-1">
                        {currentStepData.examples.map((example, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground pl-4 border-l-2 border-mint">
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tips */}
                  {currentStepData.tips && (
                    <div className="bg-coral/10 border border-coral/30 rounded-lg p-3 mb-4">
                      <p className="text-sm text-coral font-medium">
                        💡 Tip: {currentStepData.tips}
                      </p>
                    </div>
                  )}

                  {/* Decision Tree Options */}
                  {isDecisionTreeStep ? (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-ocean-blue mb-3">{t('labs.chooseOption')}</p>
                      <div className="space-y-3">
                        {currentStepData.options.map((option, idx) => (
                          <motion.div
                            key={option.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                          >
                            <button
                              onClick={() => handleOptionSelect(option)}
                              className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                                selectedOption?.id === option.id
                                  ? 'border-ocean-blue bg-ocean-blue/5 shadow-lg'
                                  : 'border-border hover:border-ocean-blue/50 hover:bg-muted/50'
                              }`}
                              data-testid={`option-${option.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                                  selectedOption?.id === option.id
                                    ? 'bg-ocean-blue text-white'
                                    : 'bg-muted text-muted-foreground'
                                }`}>
                                  {option.id}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-medium ${
                                    selectedOption?.id === option.id ? 'text-ocean-blue' : 'text-foreground'
                                  }`}>
                                    {option.text}
                                  </p>
                                  {option.type && selectedOption?.id === option.id && (
                                    <Badge variant="outline" className="mt-2 text-xs">
                                      {option.type.replace('_', ' ')}
                                    </Badge>
                                  )}
                                </div>
                                {selectedOption?.id === option.id && (
                                  <CheckCircle className="w-5 h-5 text-ocean-blue flex-shrink-0" />
                                )}
                              </div>
                            </button>
                          </motion.div>
                        ))}
                      </div>

                      {/* Option Consequence Display */}
                      <AnimatePresence>
                        {showConsequence && optionConsequence && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -10, height: 0 }}
                            className="mt-4"
                          >
                            <div className={`rounded-xl p-4 border ${
                              optionConsequence.severity === 'critical' 
                                ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800' 
                                : optionConsequence.severity === 'high'
                                ? 'bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800'
                                : optionConsequence.severity === 'medium'
                                ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800'
                                : 'bg-mint/20 border-mint'
                            }`}>
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  optionConsequence.severity === 'critical' 
                                    ? 'bg-red-100 dark:bg-red-900' 
                                    : optionConsequence.severity === 'high'
                                    ? 'bg-orange-100 dark:bg-orange-900'
                                    : optionConsequence.severity === 'medium'
                                    ? 'bg-yellow-100 dark:bg-yellow-900'
                                    : 'bg-mint'
                                }`}>
                                  {optionConsequence.severity === 'critical' ? (
                                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                  ) : optionConsequence.severity === 'high' ? (
                                    <Zap className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                  ) : (
                                    <Target className="w-4 h-4 text-ocean-blue" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-semibold text-sm mb-1 ${
                                    optionConsequence.severity === 'critical' 
                                      ? 'text-red-700 dark:text-red-400' 
                                      : optionConsequence.severity === 'high'
                                      ? 'text-orange-700 dark:text-orange-400'
                                      : 'text-ocean-blue'
                                  }`}>
                                    {optionConsequence.title}
                                  </p>
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {optionConsequence.description}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <ArrowRight className="w-3 h-3" />
                                    <span>{optionConsequence.nextHint}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Also allow text input for custom response */}
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground mb-2">{t('labs.describeScenario')}</p>
                        <Textarea
                          value={currentInput}
                          onChange={(e) => setCurrentInput(e.target.value)}
                          placeholder="Describe a real situation you're facing..."
                          className="min-h-[80px]"
                          data-testid="lab-input-custom"
                        />
                      </div>
                    </div>
                  ) : (
                    /* Standard text response input */
                    <Textarea
                      value={currentInput}
                      onChange={(e) => setCurrentInput(e.target.value)}
                      placeholder="Type your response here..."
                      className="min-h-[150px] mb-4"
                      data-testid="lab-input"
                    />
                  )}

                  {/* AI Feedback */}
                  <AnimatePresence>
                    {feedback && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-mint/20 border border-mint rounded-lg p-4 mb-4"
                      >
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-ocean-blue flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-ocean-blue text-sm mb-1">{t('labs.feedback')}</p>
                            <p className="text-sm text-foreground">{feedback}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Navigation */}
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="outline"
                      onClick={handlePreviousStep}
                      disabled={currentStep === 0 || submitting}
                      data-testid="prev-step"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('common.previous')}
                    </Button>
                    
                    {isDecisionTreeStep ? (
                      <Button
                        onClick={selectedOption ? handleSubmitOptionStep : handleSubmitStep}
                        disabled={submitting || (!selectedOption && !currentInput.trim())}
                        className="gradient-ocean"
                        data-testid="submit-step"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ChevronRight className="w-4 h-4 mr-2" />
                        )}
                        {currentStep < lab.steps.length - 1 ? t('common.continue') : t('labs.completeLab')}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmitStep}
                        disabled={submitting || !currentInput.trim()}
                        className="gradient-ocean"
                        data-testid="submit-step"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        {currentStep < lab.steps.length - 1 ? t('labs.submitContinue') : t('labs.completeLab')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Step Progress Dots */}
              <div className="flex justify-center gap-2">
                {lab.steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index < currentStep 
                        ? 'bg-mint' 
                        : index === currentStep 
                        ? 'bg-ocean-blue' 
                        : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default LabPage;
